import { buildDocumentSummary } from '@repo/zod-schemas/driver-onboarding'
import { PhaseProgress } from '@/components/dashboard/PhaseProgress'
import { SignAgreementButton } from '@/components/dashboard/SignAgreementButton'
import { StatusCard } from '@/components/dashboard/StatusCard'
import {
  canEditCarrierAddress as canEditCarrierAddressFor,
  getCoiCardOverride,
  isOnboardingUnfinished,
} from '@/components/dashboard/dashboardState'
import {
  canEditDriverProfile,
  PHASES,
  STATUS_CONFIG,
  getAgreementModeOverride,
  type Phase,
} from '@/components/dashboard/statusConfig'
import { OnboardingNotStarted } from '@/components/onboarding/OnboardingNotStarted'
import { isSubmitted } from '@repo/zod-schemas/driver-onboarding'
import { StepDocuments } from '@/components/onboarding/StepDocuments'
import { useUpdateProfile } from '@/hooks/mutations/onboarding/useUpdateProfile'
import { isOwnerOperatorFlow } from '@repo/constants'
import { readFormattedAddress, toPhysicalAddressJsonb } from '@repo/zod-schemas'
import { useDocumentTypes } from '@/hooks/queries/onboarding/useDocumentTypes'
import { useDriverOnboarding } from '@/hooks/queries/onboarding/useDriverOnboarding'
import { useSignatureRequest } from '@/hooks/queries/onboarding/useSignatureRequest'
import { useTenantNavigation } from '@/hooks/useTenantNavigation'
import {
  DRIVER_APP_STORE_URL,
  DRIVER_PLAY_STORE_URL,
  isApplicantUploadSource,
} from '@repo/constants'
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  CenteredLoading,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Field,
  Input,
  Label,
} from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDownIcon, LoaderCircleIcon, PencilIcon, SmartphoneIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/carriers/$slug/driver-onboarding/dashboard')({
  component: DashboardPage,
  staticData: {
    title: 'route.dashboard',
  },
})

function DashboardPage() {
  const { t } = useTranslation('ui')
  const { slug, navigate } = useTenantNavigation()
  const { data, isLoading } = useDriverOnboarding()
  const { data: docTypesData } = useDocumentTypes()
  const updateProfile = useUpdateProfile()
  const [editing, setEditing] = useState(false)
  // `null` until the driver touches the card, so the default can follow the data
  // rather than being frozen at mount — `docTypesData` is still loading on the
  // first render, and a `useState(hasRejected)` initialiser would evaluate once
  // against `undefined` and never reopen when the rejection arrives. Once they
  // toggle it themselves their choice wins and stops moving under them.
  const [documentsOpenOverride, setDocumentsOpenOverride] = useState<boolean | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [carrierAddressError, setCarrierAddressError] = useState<string | undefined>(undefined)

  const isSignaturePending = data?.driver?.status === 'signature_pending'
  const { data: signatureData } = useSignatureRequest(isSignaturePending, slug)

  const canEditProfile = canEditDriverProfile(data?.driver?.status)
  const showCarrierAddress = isOwnerOperatorFlow(data?.application?.flowKey ?? '')
  // Editable past the application phase but not past a terminal outcome (INS-42).
  const canEditCarrierAddress = canEditCarrierAddressFor({
    flowKey: data?.application?.flowKey,
    driverStatus: data?.driver?.status,
    phase: data?.application?.phase,
  })
  const canEdit = canEditProfile || canEditCarrierAddress
  const carrierAddressOnFile = readFormattedAddress(data?.driver?.physicalAddress)

  // A terminal application must not be swept back into the wizard (PET-3816).
  const onboardingUnfinished = isOnboardingUnfinished(data?.application)

  useEffect(() => {
    if (onboardingUnfinished) {
      navigate({
        to: '/carriers/$slug/driver-onboarding/onboarding',
        params: { slug },
      })
    }
  }, [onboardingUnfinished, navigate, slug])

  if (isLoading) return <CenteredLoading />

  if (!data?.driver) return <OnboardingNotStarted />
  if (onboardingUnfinished) return null

  const { driver } = data
  const baseStatusConfig = STATUS_CONFIG[driver.status ?? 'pending_application']

  if (!baseStatusConfig) return null

  // Resolved against the certificate's own status — see `getCoiCardOverride`.
  const coiCardOverride = getCoiCardOverride(driver.status, docTypesData?.types)
  const paperCardOverride = getAgreementModeOverride(driver.status, data.agreementMode)
  const withCoi = coiCardOverride
    ? { ...baseStatusConfig, ...coiCardOverride, action: undefined }
    : baseStatusConfig
  const statusConfig = paperCardOverride ? { ...withCoi, ...paperCardOverride } : withCoi

  const isTerminal = statusConfig.variant === 'terminal'
  const isSuccess = statusConfig.variant === 'success'

  // One line for a collapsed card. What it says, and why collapsing is safe,
  // lives in `buildDocumentSummary`; this only feeds it the driver's own
  // uploads and their state. `isSubmitted` stays the single answer to "did this
  // arrive", shared with the wizard's Next gate.
  const documentSummary = buildDocumentSummary(
    docTypesData?.types
      .filter((type) => isApplicantUploadSource(type.source))
      .map((type) => ({
        slug: type.slug,
        isRequired: type.isRequired,
        oneOfGroup: type.oneOfGroup,
        isSuperseded: type.isSuperseded,
        isSubmitted: isSubmitted(type),
        isApproved: type.document?.status === 'approved',
        isRejected: type.document?.status === 'rejected',
      })),
  )

  // A rejection is the one state the driver has to act on and cannot act on from
  // the header, so that card opens itself. Everything else stays collapsed.
  const documentsOpen = documentsOpenOverride ?? documentSummary?.variant === 'error'

  // Hide the "Documents" phase on the progress bar when the office required
  // zero driver-uploadable documents for this driver's insurance path. Uses
  // the same source-of-truth filter as the wizard and StepReview.
  const docTypes = docTypesData?.types
  const hasDriverUploadableRequiredDocs =
    !docTypes || docTypes.some((type) => isApplicantUploadSource(type.source) && type.isRequired)
  const visiblePhases: readonly Phase[] = PHASES.filter(
    (phase) => phase !== 'document_review' || hasDriverUploadableRequiredDocs,
  )

  // Docs hidden while the status still reads document_review (e.g.
  // application_submitted on a no-docs flow) would leave the progress bar
  // mispositioned, so snap back to the visible neighbour.
  const resolveCurrentPhase = (): Phase => {
    const phase = statusConfig.phase
    if (!visiblePhases.includes(phase) && phase === 'document_review') return 'application'
    return phase
  }
  const currentPhase = resolveCurrentPhase()

  const startEditing = () => {
    setForm({
      firstName: driver.firstName ?? '',
      lastName: driver.lastName ?? '',
      phone: driver.phone ?? '',
      dateOfBirth: driver.dateOfBirth ?? '',
      carrierAddress: readFormattedAddress(driver.physicalAddress) ?? '',
    })
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setForm({})
    setCarrierAddressError(undefined)
  }

  const handleSave = () => {
    // Mirrors the wizard: validated on press rather than disabling Save, so the
    // operator is told WHY rather than left with a dead button.
    if (canEditCarrierAddress && !form.carrierAddress?.trim()) {
      setCarrierAddressError(t('dashboard.info.carrierAddressRequired'))
      return
    }
    setCarrierAddressError(undefined)
    const payload: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(form)) {
      // Free text in the form, JSONB on the row: it travels under its column
      // name through the shared writer, not as a bare string under the draft key.
      if (key === 'carrierAddress') continue
      if (value) payload[key] = value
    }
    const address = toPhysicalAddressJsonb(form.carrierAddress)
    if (address) payload['physicalAddress'] = address
    updateProfile.mutate(payload as Parameters<typeof updateProfile.mutate>[0], {
      onSuccess: () => setEditing(false),
    })
  }

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  // Read-only fields shown always
  const readOnlyFields = [
    { labelKey: 'dashboard.info.email', value: driver.email },
    { labelKey: 'dashboard.info.dotNumber', value: driver.dotNumber },
    { labelKey: 'dashboard.info.mcNumber', value: driver.mcNumber },
    { labelKey: 'dashboard.info.companyName', value: driver.companyName },
    { labelKey: 'dashboard.info.companyLegalName', value: driver.companyLegalName },
    { labelKey: 'dashboard.info.ein', value: driver.ein },
    { labelKey: 'dashboard.info.serviceArea', value: driver.serviceArea },
    {
      labelKey: 'dashboard.info.insurance',
      value: driver.insurancePath ? t(`dashboard.info.path.${driver.insurancePath}`) : null,
    },
  ].filter((item) => item.value)

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <h1 className="text-2xl font-bold">{t('dashboard.greeting', { name: driver.firstName })}</h1>

      {/* Phase Progress */}
      <Card>
        <CardContent>
          <PhaseProgress
            currentPhase={currentPhase}
            isTerminal={isTerminal}
            isSuccess={isSuccess}
            phases={visiblePhases}
          />
        </CardContent>
      </Card>

      {/* Status Card */}
      <StatusCard config={statusConfig} reason={data.application?.rejectionReason ?? null} />

      {/* App Download — shown when approved */}
      {driver.status === 'approved_for_dispatch' && (
        <Card>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="shrink-0 rounded-full bg-primary/10 p-2.5">
                <SmartphoneIcon className="size-5 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <h2 className="text-base font-semibold">{t('dashboard.downloadApp.title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.downloadApp.description')}
                </p>
                <div className="flex gap-3 pt-1">
                  <Button variant="outline" size="sm" asChild>
                    <a href={DRIVER_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                      {t('dashboard.downloadApp.ios')}
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={DRIVER_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                      {t('dashboard.downloadApp.android')}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature UI */}
      {isSignaturePending &&
        signatureData?.signatureRequest &&
        (signatureData.signatureRequest.status === 'declined' ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-destructive">
                {t('dashboard.card.signatureDeclined.heading')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('dashboard.card.signatureDeclined.description')}
              </p>
            </CardContent>
          </Card>
        ) : signatureData.signatureRequest.signingUrl ? (
          <SignAgreementButton signingUrl={signatureData.signatureRequest.signingUrl} />
        ) : null)}

      {/* Driver Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('dashboard.info.title')}</CardTitle>
          {!editing && canEdit && (
            <CardAction>
              <Button variant="ghost" size="sm" onPress={startEditing}>
                <PencilIcon className="size-3.5" />
                {t('dashboard.info.edit')}
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-4">
              {canEditProfile && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label>{t('dashboard.info.firstName')}</Label>
                      <Input
                        value={form.firstName ?? ''}
                        onChange={(e) => updateField('firstName', e.target.value)}
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label>{t('dashboard.info.lastName')}</Label>
                      <Input
                        value={form.lastName ?? ''}
                        onChange={(e) => updateField('lastName', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid gap-1.5">
                    <Label>{t('dashboard.info.phone')}</Label>
                    <Input
                      value={form.phone ?? ''}
                      onChange={(e) => updateField('phone', e.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>{t('dashboard.info.dateOfBirth')}</Label>
                    <Input
                      type="date"
                      value={form.dateOfBirth ?? ''}
                      onChange={(e) => updateField('dateOfBirth', e.target.value)}
                    />
                  </div>
                </>
              )}
              {canEditCarrierAddress && (
                <Field error={carrierAddressError} required>
                  <Field.Label>{t('dashboard.info.carrierAddress')}</Field.Label>
                  <Input
                    value={form.carrierAddress ?? ''}
                    onChange={(e) => updateField('carrierAddress', e.target.value)}
                  />
                  <Field.Description>{t('dashboard.info.carrierAddressHelp')}</Field.Description>
                  <Field.Error />
                </Field>
              )}

              {/* Read-only fields shown below during edit */}
              {readOnlyFields.length > 0 && (
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-4">
                  {readOnlyFields.map((item) => (
                    <div key={item.labelKey} className="min-w-0">
                      <dt className="text-xs text-muted-foreground">{t(item.labelKey)}</dt>
                      <dd className="text-sm font-medium wrap-break-word">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <div className="flex gap-3 pt-2">
                <Button onPress={handleSave} isDisabled={updateProfile.isPending}>
                  {updateProfile.isPending && <LoaderCircleIcon className="size-4 animate-spin" />}
                  {t('dashboard.info.save')}
                </Button>
                <Button variant="outline" onPress={cancelEditing}>
                  {t('dashboard.info.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">{t('dashboard.info.name')}</dt>
                <dd className="text-sm font-medium wrap-break-word">
                  {driver.firstName} {driver.lastName}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">{t('dashboard.info.email')}</dt>
                <dd className="text-sm font-medium wrap-break-word">{driver.email}</dd>
              </div>
              {driver.phone && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t('dashboard.info.phone')}</dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.phone}</dd>
                </div>
              )}
              {driver.dateOfBirth && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">
                    {t('dashboard.info.dateOfBirth')}
                  </dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.dateOfBirth}</dd>
                </div>
              )}
              {driver.dotNumber && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t('dashboard.info.dotNumber')}</dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.dotNumber}</dd>
                </div>
              )}
              {driver.mcNumber && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t('dashboard.info.mcNumber')}</dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.mcNumber}</dd>
                </div>
              )}
              {driver.companyName && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">
                    {t('dashboard.info.companyName')}
                  </dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.companyName}</dd>
                </div>
              )}
              {driver.companyLegalName && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">
                    {t('dashboard.info.companyLegalName')}
                  </dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.companyLegalName}</dd>
                </div>
              )}
              {driver.ein && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t('dashboard.info.ein')}</dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.ein}</dd>
                </div>
              )}
              {driver.serviceArea && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">
                    {t('dashboard.info.serviceArea')}
                  </dt>
                  <dd className="text-sm font-medium wrap-break-word">{driver.serviceArea}</dd>
                </div>
              )}
              {driver.insurancePath && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t('dashboard.info.insurance')}</dt>
                  <dd className="text-sm font-medium wrap-break-word">
                    {t(`dashboard.info.path.${driver.insurancePath}`)}
                  </dd>
                </div>
              )}
              {/* Deliberately NOT hidden when empty, unlike every field above:
                  a missing carrier address is what blocks the agreement
                  (INS-42), so it is the one value the operator most needs to
                  see the absence of. */}
              {showCarrierAddress && (
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">
                    {t('dashboard.info.carrierAddress')}
                  </dt>
                  <dd
                    className={
                      carrierAddressOnFile
                        ? 'text-sm font-medium wrap-break-word'
                        : 'text-sm font-medium wrap-break-word text-destructive'
                    }
                  >
                    {carrierAddressOnFile ?? t('dashboard.info.carrierAddressMissing')}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Documents — the dashboard is where the driver lands once `/complete`
          sets currentStep to `dispatch`, which puts the wizard (and with it the
          only upload surface) permanently out of reach. A voided check that
          arrives after everything else, or a mis-tapped Submit part way through
          a run of photos, then had nowhere to go (PET-3194). The API never
          gated uploads on the application phase — only on the document's own
          status — so the slots stay live here with no server change. */}
      <Card>
        {/* Collapsed by default. Once onboarding is done this list is a long
            wall of approved slots the driver has no reason to reread, so the
            card leads with the count and opens on demand. The summary is what
            keeps that safe: anything still owed or turned down is named in the
            header, so a collapsed card never hides work. */}
        <Collapsible open={documentsOpen} onOpenChange={setDocumentsOpenOverride}>
          <CollapsibleTrigger className="w-full cursor-pointer text-left">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <span>{t('wizard.step.documents.title')}</span>
                {documentSummary && (
                  <Badge size="xs" variant={documentSummary.variant}>
                    {t(documentSummary.labelKey, documentSummary.labelValues)}
                  </Badge>
                )}
                <ChevronDownIcon
                  aria-hidden="true"
                  className={`ml-auto size-4 shrink-0 text-muted-foreground transition-transform ${
                    documentsOpen ? 'rotate-180' : ''
                  }`}
                />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <StepDocuments />
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  )
}
